// Plugin parameters in Quick actions: start the plugin, ask for each parameter (with the
// plugin's own suggestions as you type), then fire figma.on('run', {parameters}).
import { app } from './app.ts';
import { onPluginSuggestions, postToPlugin, runPlugin, type PluginDef, type PluginParameter } from './plugins.ts';

export interface ParamPrompt {
  plugin: string;
  current: PluginParameter;
  freeform: boolean;
  suggestions: { name: string; data: unknown }[];
  message?: string;
  /** The person typed: ask the plugin for suggestions. */
  query(q: string, set: (p: ParamPrompt | null) => void): void;
  /** A value was picked: next parameter, or run. */
  choose(s: { name: string; data: unknown }, q: string, set: (p: ParamPrompt | null) => void): void;
}

export function pluginParameters(def: PluginDef, done: () => void, set: (p: ParamPrompt | null) => void): ParamPrompt {
  const params = def.parameters ?? [];
  const values: Record<string, unknown> = {};
  runPlugin(def);
  let prompt!: ParamPrompt;
  let setter: (p: ParamPrompt | null) => void = set;
  const ask = (i: number, q: string) => postToPlugin({ __vellum: 'paramInput', key: params[i].key, query: q, parameters: { ...values } });
  const make = (i: number): ParamPrompt => ({
    plugin: def.name,
    current: params[i],
    freeform: !!params[i].allowFreeform || !!params[i].optional,
    suggestions: [],
    query(q, set) {
      setter = set;
      ask(i, q);
    },
    choose(s, _q, set) {
      values[params[i].key] = s.data;
      if (i + 1 < params.length) {
        prompt = make(i + 1);
        set(prompt);
        setter = set;
        ask(i + 1, '');
        return;
      }
      set(null);
      done();
      postToPlugin({ __vellum: 'run', command: '', parameters: values });
      app.toast(`Running “${def.name}”`);
    },
  });
  prompt = make(0);
  onPluginSuggestions((key, list, message) => {
    if (key !== prompt.current.key) return;
    prompt = { ...prompt, suggestions: list, message };
    setter(prompt);
  });
  // the plugin needs a moment to load and register its input handler
  setTimeout(() => ask(0, ''), 150);
  return prompt;
}
